/**
 * The EnvironmentService is a simple static class for deriving what the
 * current, active environment is based on a given URL.
 *
 * It exposes a static getter for the currentEnvironment, which will attempt to resolve this value off
 * the window object. And, also exposes a `getEnvironmentForLocation` as a fallback for in case
 * an environment needs to be derived from somewhere else
 */
export class EnvironmentService {
  static ENV_RE = /(\w+)(?:\.www)?\.service\.vic\.gov\.au/;

  /**
   * Returns the derived environment for a given location string
   *
   * @param locationHref The fullly qualified URL to derive the environment from
   * @returns the active environment for the given URL, with the production environment returning 'prod'
   */
  static getEnvironmentForLocation(locationHref: string) {
    // default the null'ish value to 'www'
    // This is so www.service.vic.gov.au & https://service.vic.gov.au return the same thing for production
    // Other environments will ignore the www as part of the regex
    const env = EnvironmentService.ENV_RE.exec(locationHref)?.[1] ?? 'www';

    // handle env == 'www' -> production
    return env === 'www' ? 'prod' : env;
  }

  /**
   * Retrieves the current environment based on the window object.
   *
   * If no window exists (e.g. server-side rendering), returns an empty string
   */
  static get currentEnvironment(): string {
    if (!window) {
      return '';
    }

    return EnvironmentService.getEnvironmentForLocation(window.location.href);
  }
}
